import React, { useState, useEffect, useMemo } from 'react'; // eslint-disable-line
import { CombinedTip } from '../interfaces/tip/CombinedTip';
import { MatchForecast, valueof } from '../interfaces/tip/MatchForecast';
import { Channel, Tip, TipType } from '../interfaces/tip/Tip';

import { SimpleTip } from '../interfaces/tip/SimpleTip';
import { MatchFromAPI } from '../interfaces/MatchFromAPI';
import { MatchForecastType } from '../MatchSwitcher/MatchSwitcher';
import { OddsType } from '../shared/InputOdds/InputOdds';

import { Bookie, Market, Sport, Stake } from '../interfaces/utils';
//MOCKS
import {
  mockFeaturedMarkets,
  mockFeaturedMarketsBySport,
} from '../shared/Mocks/Markets';
import { mockFeaturedStakes } from '../shared/Mocks/Stakes';
import { mockFeaturedBookies } from '../shared/Mocks/Bookies';
import { mockFeaturedSports } from '../shared/Mocks/Sports';
import { MarketsBySport, MarketsState } from '../../../store/markets/reducer';
import { BookiesState } from '../../../store/bookies/reducer';
import { SportsState } from '../../../store/sports/reducer';
import { TipsInfoState } from '../../../store/tips/reducer';
import { TipsterPreferencesState } from '../../../store/tipsterPreferences/reducer';
import { ChannelsState } from '../../../store/channels/reducer';

interface TipsterPreferencesOwnState {
  featuredStakes: Stake[];
  featuredBookies: Bookie[];
  featuredSports: Sport[];
  featuredMarkets: Market[];
  featuredMarketsBySport: MarketsBySport;
}
interface UseTipPublishProps {
  marketsState: MarketsState;
  requestMarkets: () => void;
  bookiesState: BookiesState;
  requestBookies: () => void;
  sportsState: SportsState;
  requestSports: () => void;
  tipsInfo: TipsInfoState;
  requestPricingTiers: () => void;
  tipType: TipType;
  channelType: Channel | undefined;
  tipsterPreferencesState: TipsterPreferencesState;
  requestTipsterPreferences: (tipsterID: string) => void;
  tipsterID: string;
  channelsState: ChannelsState;
  requestChannels: (tipsterID: string) => void;
}
export const useTipPublish = ({
  marketsState,
  requestMarkets,
  bookiesState,
  requestBookies,
  sportsState,
  requestSports,
  tipsInfo,
  requestPricingTiers,
  tipType,
  channelType,
  tipsterPreferencesState,
  requestTipsterPreferences,
  tipsterID,
  channelsState,
  requestChannels,
}: UseTipPublishProps) => {
  const [tipsterPreferences, setTipsterPreferences] =
    useState<TipsterPreferencesOwnState>({
      featuredMarkets: mockFeaturedMarkets,
      featuredStakes: mockFeaturedStakes,
      featuredBookies: mockFeaturedBookies,
      featuredSports: mockFeaturedSports,
      featuredMarketsBySport: mockFeaturedMarketsBySport,
    });
  const {
    featuredStakes,
    featuredMarkets,
    featuredSports,
    featuredBookies,
    featuredMarketsBySport,
  } = tipsterPreferences;

  const defaultMatchForecastCreator = (index?: number) =>
    new MatchForecast(
      MatchForecastType.DEFAULT,
      featuredMarkets[0],
      undefined,
      index,
    );
  const customMatchForecastCreator = (index?: number) =>
    new MatchForecast(
      MatchForecastType.CUSTOM,
      featuredMarkets[0],
      featuredSports[0],
      index,
    );
  const unsetChannel: Channel = {
    id: tipsterID,
    type: 'UNSET',
    icon_url: '',
    title: 'unset',
    publishable_tip_types: ['FREE', 'PREMIUM', 'PAID'],
  };
  const [tip, setTip] = useState<SimpleTip | CombinedTip>(
    tipType === 'SIMPLE'
      ? new SimpleTip(
          defaultMatchForecastCreator(),
          featuredBookies[0],
          featuredStakes[0],
          channelType || unsetChannel,
        )
      : new CombinedTip(
          [defaultMatchForecastCreator(), defaultMatchForecastCreator(1)],
          featuredBookies[0],
          featuredStakes[0],
          channelType || unsetChannel,
        ),
  );
  const [reloadCount, setReloadCount] = useState(0);
  const [validationsReloadCount, setValidationsReloadCount] = useState(0);
  const [showValidations, setShowValidations] = useState<boolean>(false);

  const formatCodes = (codes: string[]) =>
    codes.map((code) => {
      return {
        code: code,
        id: code,
        name: code,
        active: true,
      };
    });

  useEffect(() => {
    tip && tip.runValidations();
    setValidationsReloadCount(validationsReloadCount + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadCount]);

  const resetTip = () => {
    setTip(
      tipType === 'SIMPLE'
        ? new SimpleTip(
            defaultMatchForecastCreator(),
            featuredBookies[0],
            featuredStakes[0],
            channelType || unsetChannel,
          )
        : new CombinedTip(
            [defaultMatchForecastCreator(), defaultMatchForecastCreator(1)],
            featuredBookies[0],
            featuredStakes[0],
            channelType || unsetChannel,
          ),
    );
  };

  const areStatesLoading = useMemo(() => {
    const isDoneLoading =
      marketsState.requested &&
      !marketsState.isLoading &&
      marketsState.data.markets &&
      !!marketsState.data.markets.length &&
      bookiesState.requested &&
      !bookiesState.isLoading &&
      bookiesState.data.bookies &&
      !!bookiesState.data.bookies.length &&
      sportsState.requested &&
      !sportsState.isLoading &&
      sportsState.data.sports &&
      !!sportsState.data.sports.length &&
      tipsterPreferencesState.requested &&
      !tipsterPreferencesState.isLoading &&
      tipsInfo.pricing &&
      !!tipsInfo.pricing.length &&
      tipsInfo.tiersRequested &&
      !tipsInfo.isLoading &&
      channelsState.requested &&
      !channelsState.isLoading &&
      !!channelsState.data.length;

    return !isDoneLoading;
  }, [
    marketsState,
    bookiesState,
    sportsState,
    tipsterPreferencesState,
    tipsInfo,
    channelsState,
  ]);

  useEffect(() => {
    if (
      tipsterPreferencesState.requested &&
      !tipsterPreferencesState.isLoading
    ) {
      const { bookies, tipster_preferences } = tipsterPreferencesState.data;
      const { sport_codes, market_codes, stakes, market_codes_by_sport } =
        tipster_preferences;

      if (
        bookies &&
        !!bookies.length &&
        sport_codes &&
        !!sport_codes.length &&
        market_codes &&
        !!market_codes.length &&
        stakes &&
        !!stakes.length &&
        market_codes_by_sport
      ) {
        setTipsterPreferences({
          featuredBookies: bookies,
          featuredSports: formatCodes(sport_codes),
          featuredMarkets: formatCodes(market_codes),
          featuredStakes: stakes,
          featuredMarketsBySport: market_codes_by_sport,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipsterPreferencesState]);

  useEffect(() => {
    if (
      tipsterPreferences.featuredBookies.length &&
      tipsterPreferences.featuredMarkets.length &&
      tipsterPreferences.featuredSports.length &&
      tipsterPreferences.featuredStakes.length
    ) {
      resetTip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipsterPreferences]);

  useEffect(() => {
    if (!marketsState.requested && !marketsState.isLoading) {
      requestMarkets();
    }
    if (!bookiesState.requested && !bookiesState.isLoading) {
      requestBookies();
    }

    if (!sportsState.requested && !sportsState.isLoading) {
      requestSports();
    }

    if (!tipsInfo.tiersRequested && !tipsInfo.isLoading) {
      requestPricingTiers();
    }
    if (!channelsState.requested && !channelsState.isLoading) {
      requestChannels(tipsterID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!tipsterPreferencesState.isLoading) {
      requestTipsterPreferences(tipsterID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipsterID]);

  const handleMatchSwitcher = (matchForecast: MatchForecast) => {
    setShowValidations(false);

    if (matchForecast.type === MatchForecastType.CUSTOM) {
      const newMatchForecast = defaultMatchForecastCreator(matchForecast.index);
      tip.setMatchForecast(newMatchForecast);
      tip.recalculateAccumulatedOdds();
      setReloadCount(reloadCount + 1);
    } else {
      const newMatchForecast = customMatchForecastCreator(matchForecast.index);
      tip.setMatchForecast(newMatchForecast);
      tip.recalculateAccumulatedOdds();
      setReloadCount(reloadCount + 1);
    }
  };

  //TIP SETTER FUNCTIONS
  const handleMatchSelection = (
    matchForecast: MatchForecast,
    matchFromAPI: MatchFromAPI,
  ) => {
    matchForecast.setValue('match_id', matchFromAPI.id);
    matchForecast.setValue('away_player', matchFromAPI.away_player);
    matchForecast.setValue('home_player', matchFromAPI.home_player);
    matchForecast.setValue('competition_name', matchFromAPI.competition_name);
    matchForecast.setValue('sport', {
      code: matchFromAPI.sport,
      name: matchFromAPI.sport,
    });
    matchForecast.setValue('start_date', matchFromAPI.start_date);

    tip.matchForecasts[matchForecast.index] = matchForecast;
    setReloadCount(reloadCount + 1);
  };

  const handleFormattedOdds = (
    matchForecast: MatchForecast,
    odds: OddsType,
  ) => {
    tip.setAccumulatedOdds(matchForecast, odds);
    setReloadCount(reloadCount + 1);
  };

  const handleMatchForecastValue = (
    matchForecast: MatchForecast,
    key: keyof MatchForecast,
    value: valueof<MatchForecast>,
  ) => {
    matchForecast.setValue(key, value);
    setReloadCount(reloadCount + 1);
  };

  const handleTipValue = (key: keyof Tip, value: valueof<Tip>) => {
    tip.setValue(key, value);
    setReloadCount(reloadCount + 1);
  };
  const handleMatchForecastReset = (matchForecast: MatchForecast) => {
    matchForecast.reset();
    setReloadCount(reloadCount + 1);
  };
  const handleAddMatchForecast = (index: number) => {
    setShowValidations(false);
    tip.addMatchForecast(defaultMatchForecastCreator(index));
    setReloadCount(reloadCount + 1);
  };
  const checkStepValidity = (stepNumber: number): boolean => {
    return tip.checkStepValidity(stepNumber);
  };
  const handleEmptyMatches = () => {
    //check for empty match and delete it
    type ForecastKeys = (keyof MatchForecast)[];

    const DEFAULT_CHECKS: ForecastKeys = ['match_id', 'tip', 'formatted_odds'];
    const CUSTOM_CHECKS: ForecastKeys = [
      'home_player',
      'away_player',
      'competition_name',
      'tip',
      'formatted_odds',
    ];
    tip.matchForecasts.forEach(
      (matchForecast: MatchForecast, index: number) => {
        if (index >= 2) {
          let isEmpty = true;
          if (matchForecast.type === MatchForecastType.DEFAULT) {
            DEFAULT_CHECKS.forEach((key) => {
              if (key === 'formatted_odds') {
                if (matchForecast.formatted_odds.decimal) {
                  isEmpty = false;
                  return;
                }
              } else if (matchForecast[key]) {
                isEmpty = false;
                return;
              }
            });
          } else {
            CUSTOM_CHECKS.forEach((key) => {
              if (key === 'formatted_odds') {
                if (matchForecast.formatted_odds.decimal) {
                  isEmpty = false;
                  return;
                }
              } else if (matchForecast[key]) {
                isEmpty = false;
                return;
              }
            });
          }
          if (isEmpty) {
            //delete match
            tip.deleteMatchForecast(index);
          }
        }
      },
    );
  };

  return {
    states: {
      tip,
      setTip,
    },
    handleMatchSelection,
    handleMatchSwitcher,
    handleTipValue,
    handleMatchForecastValue,
    handleFormattedOdds,
    handleMatchForecastReset,
    handleAddMatchForecast,
    showValidations,
    setShowValidations,
    checkStepValidity,
    featuredMarkets,
    featuredMarketsBySport,
    featuredBookies,
    featuredSports,
    featuredStakes,
    areStatesLoading,
    handleEmptyMatches,
  };
};
